import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Modal, ModalBody, ModalHeader, Spinner, Button,
} from 'reactstrap'
import { Document, Page } from 'react-pdf'
import { getRegs } from '../../../utils/fc'

const DEFAULT_FILE_BASE = 'https://oikos.ecoss.is/oikos/'

const DOC_FIELDS = {
  '(SELECT documento_desc FROM documento WHERE documento.documento_id = documentoVinc.documento_id)': 'documento_desc',
  '(SELECT tpDocumento_id FROM documento WHERE documento.documento_id = documentoVinc.documento_id)': 'tpDocumento_id',
  '(SELECT tpDocumento_desc FROM tpDocumento WHERE tpDocumento.tpDocumento_id = (SELECT tpDocumento_id FROM documento WHERE documento.documento_id = documentoVinc.documento_id LIMIT 1))': 'tpDocumento_desc',
  '(SELECT arquivo_caminho FROM arquivo WHERE arquivo.arquivo_tabRef = "documento" AND arquivo.arquivo_tabId = documentoVinc.documento_id ORDER BY arquivo_id DESC LIMIT 1)': 'arquivo_caminho',
}

const isPdfPath = (path) => {
  if (!path) return false
  const clean = String(path).split('?')[0].split('#')[0].toLowerCase()
  return clean.endsWith('.pdf')
}

const buildFileUrl = (path, fileBaseUrl) => {
  if (!path) return null
  const p = String(path)
  if (/^https?:\/\//i.test(p)) return p
  const base = fileBaseUrl || DEFAULT_FILE_BASE
  return `${base.replace(/\/?$/, '/')}${p.replace(/^\//, '')}`
}

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number') return String(value)
  return String(value)
}

/**
 * Modal reutilizável para visualizar PDFs vinculados a qualquer registro Oikos
 * (documentoVinc: tabRef + tabId).
 *
 * Props:
 * - open, onClose
 * - tabRef, tabId  → chave da vinculação (ex.: 'bdro', 123)
 * - title
 * - recordFields   → [{ label, value }] dados principais do registro
 * - fileBaseUrl    → prefixo dos arquivos (default oikos.ecoss.is)
 * - files          → lista opcional pré-carregada (pula o fetch)
 * - initialIndex
 */
const ModalOikosDocsViewer = ({
  open,
  onClose,
  tabRef,
  tabId,
  title = 'Documentos',
  recordFields = [],
  fileBaseUrl = DEFAULT_FILE_BASE,
  files: filesProp,
  initialIndex = 0,
}) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [files, setFiles] = useState([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [numPages, setNumPages] = useState(null)
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const dragOrigin = useRef(null)
  const viewportRef = useRef(null)

  const loadFiles = useCallback(async () => {
    if (Array.isArray(filesProp)) {
      const normalized = filesProp
        .map((f, idx) => ({
          id: f.id || f.documento_id || f.documentoVinc_id || idx,
          label: f.label || f.documento_desc || f.tpDocumento_desc || `Arquivo ${idx + 1}`,
          typeLabel: f.typeLabel || f.tpDocumento_desc || null,
          path: f.path || f.arquivo_caminho || null,
          url: f.url || buildFileUrl(f.path || f.arquivo_caminho, fileBaseUrl),
        }))
        .filter((f) => f.url && isPdfPath(f.path || f.url))
      setFiles(normalized)
      setActiveIndex(Math.min(Math.max(initialIndex, 0), Math.max(normalized.length - 1, 0)))
      return
    }

    if (!tabRef || !tabId) {
      setFiles([])
      return
    }

    setLoading(true)
    setError(null)
    try {
      const rows = await getRegs('documentoVinc', {
        documentoVinc_tabRef: tabRef,
        documentoVinc_tabId: tabId,
      }, DOC_FIELDS)

      const normalized = (rows || [])
        .map((row, idx) => ({
          id: row.documentoVinc_id || row.documento_id || idx,
          label: row.documento_desc || row.tpDocumento_desc || `Documento ${idx + 1}`,
          typeLabel: row.tpDocumento_desc || null,
          path: row.arquivo_caminho || null,
          url: buildFileUrl(row.arquivo_caminho, fileBaseUrl),
        }))
        .filter((f) => f.url && isPdfPath(f.path))

      setFiles(normalized)
      setActiveIndex(Math.min(Math.max(initialIndex, 0), Math.max(normalized.length - 1, 0)))
    } catch (err) {
      console.error('ModalOikosDocsViewer load error', err)
      setError('Não foi possível carregar os documentos.')
      setFiles([])
    } finally {
      setLoading(false)
    }
  }, [filesProp, tabRef, tabId, fileBaseUrl, initialIndex])

  useEffect(() => {
    if (!open) return undefined
    loadFiles()
    setScale(1)
    setOffset({ x: 0, y: 0 })
    setNumPages(null)
    return undefined
  }, [open, loadFiles])

  useEffect(() => {
    setScale(1)
    setOffset({ x: 0, y: 0 })
    setNumPages(null)
  }, [activeIndex])

  const activeFile = files[activeIndex] || null

  const onDocumentLoadSuccess = ({ numPages: next }) => {
    setNumPages(next)
  }

  const zoomBy = (delta) => {
    setScale((prev) => {
      const next = Math.round((prev + delta) * 100) / 100
      return Math.min(3, Math.max(0.5, next))
    })
  }

  const resetView = () => {
    setScale(1)
    setOffset({ x: 0, y: 0 })
  }

  const onPointerDown = (e) => {
    if (e.button !== 0) return
    setDragging(true)
    dragOrigin.current = {
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e) => {
    if (!dragging || !dragOrigin.current) return
    setOffset({
      x: e.clientX - dragOrigin.current.x,
      y: e.clientY - dragOrigin.current.y,
    })
  }

  const onPointerUp = (e) => {
    setDragging(false)
    dragOrigin.current = null
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }

  const summaryFields = useMemo(
    () => (recordFields || []).filter((f) => f && f.label),
    [recordFields],
  )

  return (
    <Modal
      isOpen={!!open}
      toggle={onClose}
      centered
      size="xl"
      zIndex={2100}
      contentClassName="oikos-docs-viewer-modal"
      style={{ maxWidth: '96vw' }}
    >
      <ModalHeader toggle={onClose} className="border-0 pb-2">
        <span style={{ fontSize: 16, fontWeight: 600 }}>{title}</span>
      </ModalHeader>
      <ModalBody className="pt-0" style={{ minHeight: '75vh' }}>
        <div
          className="d-flex"
          style={{
            gap: 12,
            minHeight: '70vh',
            height: '70vh',
          }}
        >
          {/* Navegador de arquivos */}
          <aside
            style={{
              width: 220,
              flexShrink: 0,
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              background: '#f8fafc',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              className="px-3 py-2"
              style={{
                borderBottom: '1px solid #e2e8f0',
                fontSize: 12,
                fontWeight: 700,
                color: '#334155',
              }}
            >
              Arquivos
              {files.length > 0 ? ` (${files.length})` : ''}
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading && (
                <div className="d-flex justify-content-center py-4">
                  <Spinner size="sm" />
                </div>
              )}
              {!loading && error && (
                <div className="p-3 text-danger" style={{ fontSize: 12 }}>{error}</div>
              )}
              {!loading && !error && files.length === 0 && (
                <div className="p-3 text-muted" style={{ fontSize: 12 }}>
                  Nenhum PDF vinculado a este registro.
                </div>
              )}
              {!loading && files.map((file, idx) => {
                const active = idx === activeIndex
                return (
                  <button
                    key={file.id}
                    type="button"
                    onClick={() => setActiveIndex(idx)}
                    className="w-100 text-left border-0"
                    style={{
                      display: 'block',
                      padding: '10px 12px',
                      background: active ? '#e0f2fe' : 'transparent',
                      borderBottom: '1px solid #e2e8f0',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: active ? 700 : 500, color: '#0f172a' }}>
                      <i className="ri-file-pdf-line mr-1" />
                      {file.label}
                    </div>
                    {file.typeLabel && (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        {file.typeLabel}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </aside>

          {/* Painel principal */}
          <section
            className="d-flex flex-column"
            style={{ flex: 1, minWidth: 0, gap: 10 }}
          >
            {/* Dados do registro */}
            {summaryFields.length > 0 && (
              <div
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  background: '#fff',
                  padding: '10px 12px',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '8px 16px',
                  }}
                >
                  {summaryFields.map((field) => (
                    <div key={field.label}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
                        {field.label}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: '#0f172a',
                          wordBreak: 'break-word',
                        }}
                        title={formatFieldValue(field.value)}
                      >
                        {formatFieldValue(field.value)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Toolbar PDF */}
            <div
              className="d-flex align-items-center justify-content-between"
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                background: '#f8fafc',
                padding: '6px 10px',
                gap: 8,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: '#334155', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {activeFile ? activeFile.label : 'Sem arquivo selecionado'}
              </div>
              <div className="d-flex align-items-center" style={{ gap: 4 }}>
                <Button size="sm" color="light" disabled={!activeFile || scale <= 0.5} onClick={() => zoomBy(-0.25)} title="Diminuir zoom">−</Button>
                <span style={{ minWidth: 52, textAlign: 'center', fontSize: 12, fontWeight: 700 }}>
                  {Math.round(scale * 100)}%
                </span>
                <Button size="sm" color="light" disabled={!activeFile || scale >= 3} onClick={() => zoomBy(0.25)} title="Aumentar zoom">+</Button>
                <Button size="sm" color="light" disabled={!activeFile} onClick={resetView} title="Resetar zoom e posição">Ajustar</Button>
                {activeFile?.url && (
                  <Button
                    size="sm"
                    color="light"
                    tag="a"
                    href={activeFile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Abrir em nova aba"
                  >
                    <i className="ri-external-link-line" />
                  </Button>
                )}
              </div>
            </div>

            {/* Viewport PDF com pan */}
            <div
              ref={viewportRef}
              style={{
                flex: 1,
                minHeight: 0,
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                background: '#0f172a0a',
                overflow: 'hidden',
                position: 'relative',
                cursor: activeFile ? (dragging ? 'grabbing' : 'grab') : 'default',
                userSelect: 'none',
              }}
              onPointerDown={activeFile ? onPointerDown : undefined}
              onPointerMove={activeFile ? onPointerMove : undefined}
              onPointerUp={activeFile ? onPointerUp : undefined}
              onPointerLeave={activeFile ? onPointerUp : undefined}
            >
              {!activeFile && (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted" style={{ fontSize: 13 }}>
                  Selecione um PDF na lista ao lado
                </div>
              )}
              {activeFile && (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                  }}
                >
                  <div
                    style={{
                      transform: `translate(${offset.x}px, ${offset.y}px)`,
                      transformOrigin: 'top left',
                      padding: 16,
                      display: 'inline-block',
                      minWidth: '100%',
                    }}
                  >
                    <Document
                      file={activeFile.url}
                      loading={
                        <div className="d-flex justify-content-center py-5">
                          <Spinner />
                        </div>
                      }
                      error={
                        <div className="text-danger p-4" style={{ fontSize: 13 }}>
                          Falha ao carregar o PDF.
                          {' '}
                          <a href={activeFile.url} target="_blank" rel="noopener noreferrer">Abrir arquivo</a>
                        </div>
                      }
                      onLoadSuccess={onDocumentLoadSuccess}
                    >
                      {numPages &&
                        Array.from({ length: numPages }, (_, i) => (
                          <div key={`page_${i + 1}`} style={{ marginBottom: 12 }}>
                            <Page
                              pageNumber={i + 1}
                              scale={scale}
                              renderTextLayer={false}
                              renderAnnotationLayer={false}
                              width={Math.max(320, Math.min(860, (viewportRef.current?.clientWidth || 860) - 48))}
                            />
                          </div>
                        ))}
                    </Document>
                  </div>
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              Arraste o PDF para mover a posição · use − / + para zoom
              {numPages ? ` · ${numPages} página${numPages > 1 ? 's' : ''}` : ''}
            </div>
          </section>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ModalOikosDocsViewer
